import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  addCarePlanTeamFailure,
  addCarePlanTeamSuccess,
  deleteCarePlanTeamFailure,
  deleteCarePlanTeamSuccess,
  editCarePlanTeamFailure,
  editCarePlanTeamSuccess,
  fetchCarePlanTeamFailure,
  fetchCarePlanTeamMemberFailure,
  fetchCarePlanTeamMemberSuccess,
  fetchCarePlanTeamSuccess,
  searchCarePlanTeamFailure,
  searchCarePlanTeamSuccess,
} from "./actions";
import {
  ADD_CARE_PLAN_TEAM,
  DELETE_CARE_PLAN_TEAM,
  EDIT_CARE_PLAN_TEAM,
  FETCH_CARE_PLAN_TEAM,
  FETCH_CARE_PLAN_TEAM_MEMBER,
  SEARCH_CARE_PLAN_TEAM,
} from "./constants";

const SETTINGS_PATH = "/d/r/settings/care-team";

const normalizeCareTeamSearchList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export function* searchCarePlanTeamSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const locationId = payload?.location_id;
  if (!locationId) {
    yield put(searchCarePlanTeamSuccess([]));
    return;
  }
  const params = { location_id: locationId };
  if (payload?.search_term && String(payload.search_term).trim()) {
    params.search_term = String(payload.search_term).trim();
  }
  const query = QueryString.stringify(params);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/care-team/search?${query}`,
    );
    yield put(searchCarePlanTeamSuccess(normalizeCareTeamSearchList(data)));
  } catch (e) {
    yield put(searchCarePlanTeamFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to search care teams"));
  }
}

export function* fetchCarePlanTeamSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  let query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(api(token).get, `/api/care-team?${query}`);
    const result = Array.isArray(data)
      ? { data, total: data.length, current_page: payload?.page ?? 1 }
      : { ...data, current_page: payload?.page ?? data?.current_page ?? 1 };
    yield put(fetchCarePlanTeamSuccess(result));
  } catch (e) {
    yield put(fetchCarePlanTeamFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch care team"));
  }
}

export function* addCarePlanTeamSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { data, history, onSuccess } = payload || {};
  try {
    const response = yield call(api(token).post, `/api/care-team/add`, data);
    if (response) {
      yield put(addCarePlanTeamSuccess());
      yield put(successAlert("Care team member created successfully"));
      if (typeof onSuccess === "function") onSuccess();
      else if (history) history.push(SETTINGS_PATH);
    }
  } catch (e) {
    yield put(addCarePlanTeamFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to create care team member"));
  }
}

export function* fetchCarePlanTeamMemberSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id =
    typeof payload === "string" ? payload : (payload?.uuid ?? payload?.id);
  try {
    const { data } = yield call(api(token).get, `/api/care-team/${id}`);
    yield put(fetchCarePlanTeamMemberSuccess(data?.data ?? data));
  } catch (e) {
    yield put(fetchCarePlanTeamMemberFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch care team member"));
  }
}

export function* editCarePlanTeamSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { data, history, id, uuid, onSuccess } = payload || {};
  const memberId = id ?? uuid;
  try {
    const response = yield call(
      api(token).post,
      `/api/care-team/edit/${memberId}`,
      data,
    );
    if (response) {
      yield put(editCarePlanTeamSuccess());
      yield put(successAlert("Care team member updated successfully"));
      if (typeof onSuccess === "function") onSuccess();
      else if (history) history.push(SETTINGS_PATH);
    }
  } catch (e) {
    yield put(editCarePlanTeamFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to update care team member"));
  }
}

export function* deleteCarePlanTeamSaga({ payload, onDelete }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id = payload?.id ?? payload?.uuid;
  try {
    const data = yield call(api(token).delete, `/api/care-team/${id}`);
    if (data) {
      yield put(deleteCarePlanTeamSuccess());
      yield put(successAlert("Care team member deleted successfully"));
      typeof onDelete === "function" && onDelete();
    }
  } catch (e) {
    yield put(deleteCarePlanTeamFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to delete care team member"));
  }
}

export default function* CarePlanTeamSaga() {
  yield all([
    takeEvery(SEARCH_CARE_PLAN_TEAM, searchCarePlanTeamSaga),
    takeEvery(FETCH_CARE_PLAN_TEAM, fetchCarePlanTeamSaga),
    takeEvery(ADD_CARE_PLAN_TEAM, addCarePlanTeamSaga),
    takeEvery(FETCH_CARE_PLAN_TEAM_MEMBER, fetchCarePlanTeamMemberSaga),
    takeEvery(EDIT_CARE_PLAN_TEAM, editCarePlanTeamSaga),
    takeEvery(DELETE_CARE_PLAN_TEAM, deleteCarePlanTeamSaga),
  ]);
}
